import { M2 } from 'twrk/M2.js';
import { XY } from 'twrk/V2.js';
import { Shdr, shdr_type } from 'twrk/Shdr.js';
import { Wbgl_mono } from 'twrk/Wbgl_mono.js';
import { wbgl_type } from 'twrk/Wbgl.js';
import { rapr2_body_t, rapr2_revolute_t } from 'twrk/Rapr2.js';

import { twrk_type } from './types.js';

//shared by every rope-hung figure (swinger, catcher, ...): a fixed hook and a chain of hinged
//capsule segments, rendered as one smooth catmull-rom curve. The figure itself provides the body
//it grips onto (grip_body/grip_anchor) and, once let go, the rope keeps swinging under its own
//inertia since it's a real body chain, not a single kinematic constraint.

let _shader: shdr_type | null = null;

function get_shader(Canvas: wbgl_type): shdr_type {
    if (!_shader) {
        _shader = Shdr.make(Canvas, {
            id: "rope_2d",
            vertex: `#version 300 es
            precision mediump float;
            in vec2 position;
            uniform vec2 u_screen;
            uniform float u_zoom;
            uniform vec2 u_pan;
            void main() {
                gl_Position = vec4((position - u_pan) * u_zoom * u_screen, 0.0, 1.0);
            }
            `,
            fragment: `#version 300 es
            precision mediump float;
            uniform vec4 u_color;
            out vec4 fragColor;
            void main() {
                fragColor = u_color;
            }
            `
        });
    }
    return _shader;
}

export const rope_presets = {
    segments: 6, //number of capsule links the rope is built from
    radius: 0.002,
    mass: 0.1, //per segment
    joint_stiff: 0, //keeps the rope roughly straight while still able to swing/whip
    joint_damp: 50, //higher damping ratio kills the springy bounce from a stiff motor
    grip_stiff: 400, //zero-rest-length spring holding the figure's grip anchor to the rope end
    grip_damp: 20,
    curve_res: 6, //b-spline samples per control-point span, for a smooth rope mesh
    color: [0.8, 0.8, 1, 1],
    debug_dots: true, //draw a marker quad at the hook, every segment joint, and the grip end, to visualize where the rope actually attaches
    debug_dot_size: 0.02,
    debug_dot_color: [1, 0, 0, 1],
};

//capsule endpoints: pos ± axis * half_height (axis points towards the body's top).
//note the twrk-local anchor convention is inverted vs these: top() is the anchor {0,-h} end,
//bot() the anchor {0,+h} end (anchors get y-flipped on their way into rapier, axis doesn't)
export const top = (b: rapr2_body_t, h: number): XY => ({ x: b.pos.x + b.axis.x * h, y: b.pos.y + b.axis.y * h });
export const bot = (b: rapr2_body_t, h: number): XY => ({ x: b.pos.x - b.axis.x * h, y: b.pos.y - b.axis.y * h });

//world position of a twrk-local joint anchor on a body, same math rapier applies to the anchors
export const anchor_world = (b: rapr2_body_t, a: XY): XY => ({
    x: b.pos.x - b.axis.y * a.x - b.axis.x * a.y,
    y: b.pos.y + b.axis.x * a.x - b.axis.y * a.y,
});

//zero-alloc uniform cubic B-spline sampler (G2/C2 continuous, unlike catmull-rom which only
//interpolates with C1 continuity): out is filled with `out.length` points spanning the whole
//curve. Out-of-range indices reflect across the endpoints (phantom points), which makes the
//curve pass exactly through the first and last control points - mere clamping leaves it
//stopping ~1/6 of a span short of each end.
const _phantom: XY = { x: 0, y: 0 };
function sample_bspline_fixed_n(points: XY[], out: XY[]) {
    const n = points.length;
    const segs = n - 1;
    const last = out.length - 1;
    const p = (i: number) => {
        if (i >= 0 && i <= segs) return points[i];
        const e = i < 0 ? 0 : segs; //endpoint to reflect across
        const m = i < 0 ? 1 : segs - 1; //its neighbour
        _phantom.x = 2 * points[e].x - points[m].x;
        _phantom.y = 2 * points[e].y - points[m].y;
        return _phantom;
    };
    for (let i = 0; i <= last; i++) {
        const tg = (i / last) * segs;
        let seg = tg | 0; if (seg > segs - 1) seg = segs - 1;
        const t = tg - seg;
        const p0 = p(seg - 1), p1 = p(seg), p2 = p(seg + 1), p3 = p(seg + 2);
        const t2 = t * t, t3 = t2 * t;
        const w0 = (-t3 + 3 * t2 - 3 * t + 1) / 6;
        const w1 = (3 * t3 - 6 * t2 + 4) / 6;
        const w2 = (-3 * t3 + 3 * t2 + 3 * t + 1) / 6;
        const w3 = t3 / 6;
        out[i].x = p0.x * w0 + p1.x * w1 + p2.x * w2 + p3.x * w3;
        out[i].y = p0.y * w0 + p1.y * w1 + p2.y * w2 + p3.y * w3;
    }
}

export type rope_input = {
    x: number;
    y_hook: number; //hook position, top end of the rope
    len: number;
    groups: number;
    grip_body: rapr2_body_t; //the figure body the rope's free end is tied to
    grip_anchor: XY; //anchor on grip_body, in its local space
};

export function rope_make(twrk: twrk_type, io: rope_input) {
    const { Canvas, Scrn, Rapr, Dbug } = twrk;
    const shader = get_shader(Canvas);
    const p = rope_presets;

    const hook = Rapr.fixed_point({ x: io.x, y: io.y_hook });

    const seg_count = p.segments;
    const h_seg = io.len / seg_count * 0.5;
    const segs: rapr2_body_t[] = [];
    let ry = io.y_hook;
    for (let i = 0; i < seg_count; i++) {
        segs.push(Rapr.capsule({
            pos: { x: io.x, y: ry + h_seg }, half_height: h_seg, radius: p.radius,
            mass: p.mass, groups: io.groups, velocity_damp: 0.01, angular_damp: 0.05,
        }));
        ry += h_seg * 2;
    }

    const joints: rapr2_revolute_t[] = [];
    joints.push(Rapr.revolute({ a: hook, b: segs[0], anchor_a: { x: 0, y: 0 }, anchor_b: { x: 0, y: -h_seg }, motor: { stiffness: p.joint_stiff, damping: p.joint_damp } }));
    for (let i = 1; i < seg_count; i++) {
        joints.push(Rapr.revolute({ a: segs[i - 1], b: segs[i], anchor_a: { x: 0, y: h_seg }, anchor_b: { x: 0, y: -h_seg }, motor: { stiffness: p.joint_stiff, damping: p.joint_damp } }));
    }
    //grip joint: a zero-rest-length spring pulls the figure's grip anchor to the rope's free end.
    //unlike a revolute it doesn't lock relative rotation, so it can't fight the figure's own joint
    //motors over the anchor's angle - it just holds the two points together. Killed on release,
    //recreated by re_grip() to reattach (e.g. replaying back to the moment of release).
    const make_grip_joint = () => Rapr.spring({
        a: segs[seg_count - 1], b: io.grip_body, anchor_a: { x: 0, y: h_seg }, anchor_b: io.grip_anchor,
        rest_length: 0, stiffness: p.grip_stiff, damping: p.grip_damp,
    });
    let grip_joint = make_grip_joint();

    //one smooth mesh through hook, every segment joint and the grip end, uniform-cubic-B-spline
    //sampled (G2/C2 continuous) so the rope reads as a continuous curve instead of stiff straight links
    const ctrl_count = seg_count + 2; //hook + one per segment-top-joint + grip end
    const ctrl: XY[] = Array.from({ length: ctrl_count }, () => ({ x: 0, y: 0 }));
    const curve_n = (ctrl_count - 1) * p.curve_res + 1;
    const curve: XY[] = Array.from({ length: curve_n }, () => ({ x: 0, y: 0 }));
    const curve_coords = new Float32Array(curve_n * 2);
    const mesh = M2.shape.line(curve_coords, p.radius * 2);
    const gl = Wbgl_mono.make(Canvas, shader, Scrn, { mesh, uniforms: { u_color: p.color, u_zoom: 1, u_pan: [0, 0] } });
    //reused scratch for the pan uniform so render() doesn't allocate a [x,y] pair every frame
    const u_pan: [number, number] = [0, 0];

    //debug: one dot per rope joint (hook, every inter-segment joint, grip end) so the true attach
    //points can be checked visually against the rendered curve
    const dot_count = seg_count + 2; //hook + one per segment-top-joint + grip end
    const dot_meshes = p.debug_dots ? Array.from({ length: dot_count }, () => M2.shape.circle({ x: 0, y: 0, size: p.debug_dot_size, resolution: 8 })) : [];
    const dots = p.debug_dots ? dot_meshes.map(m =>
        Wbgl_mono.make(Canvas, shader, Scrn, { mesh: m, uniforms: { u_color: p.debug_dot_color, u_zoom: 1, u_pan: [0, 0] } })
    ) : [];

    let released = false;

    return {
        hook, segs, h_seg,
        release: () => { if (!released) { released = true; grip_joint.kill(); } },
        re_grip: () => { if (released) { released = false; grip_joint = make_grip_joint(); } },
        sync_read: () => { for (const b of segs) b.sync_read(); },
        render: (zoom: number, pan: XY) => {
            //while still attached, end exactly at the grip anchor (not the rope's own last-segment end) so
            //there's no visible gap; once released, fall back to the rope's own end so it doesn't keep
            //chasing the now-disconnected, freely falling figure.
            ctrl[0].x = hook.pos.x; ctrl[0].y = hook.pos.y;
            for (let i = 0; i < segs.length; i++) {
                const j = top(segs[i], h_seg);
                ctrl[i + 1].x = j.x; ctrl[i + 1].y = j.y;
            }
            const end = released ? top(segs[segs.length - 1], h_seg) : anchor_world(io.grip_body, io.grip_anchor);
            ctrl[ctrl.length - 1].x = end.x;
            ctrl[ctrl.length - 1].y = end.y;
            sample_bspline_fixed_n(ctrl, curve);
            for (let i = 0; i < curve.length; i++) {
                curve_coords[i * 2] = curve[i].x;
                curve_coords[i * 2 + 1] = curve[i].y;
            }
            //curve_n is fixed for the rope's lifetime, so the mesh never changes length — write the
            //line straight into the existing buffer (out-param) instead of allocating a fresh one
            //every frame just to copy it in
            M2.shape.line(curve_coords, p.radius * 2, mesh);
            gl.mesh_update(mesh);
            gl.uniforms.u_zoom = zoom;
            u_pan[0] = pan.x; u_pan[1] = pan.y;
            gl.uniforms.u_pan = u_pan;
            gl.render();

            if (p.debug_dots && Dbug.on) {
                const pts: XY[] = [{ x: hook.pos.x, y: hook.pos.y }];
                for (const s of segs) pts.push(top(s, h_seg));
                pts.push(end);
                for (let i = 0; i < dots.length; i++) {
                    const pt = pts[i];
                    if (!pt) continue;
                    M2.shape.circle({ x: pt.x, y: pt.y, size: p.debug_dot_size, resolution: 8, mesh: dot_meshes[i] });
                    dots[i].mesh_update(dot_meshes[i]);
                    dots[i].uniforms.u_zoom = zoom;
                    dots[i].uniforms.u_pan = [pan.x, pan.y];
                    dots[i].render();
                }
            }
        },
        kill: () => {
            gl.kill();
            for (const d of dots) d.kill();
            if (!released) grip_joint.kill();
            for (const j of joints) j.kill();
            for (const b of segs) b.kill();
            hook.kill();
        },
    };
}

export type rope_t = ReturnType<typeof rope_make>;
